import { useEffect, useState } from 'react';
import { useCalendar, type CalendarEvent } from '@/state/calendar';
import { cn } from '@/lib/cn';

const CATEGORY_COLOR: Record<CalendarEvent['category'], string> = {
  macro: 'var(--accent-primary)',
  earnings: 'var(--positive)',
  rally: 'var(--warning)',
  personal: 'var(--negative)',
  court: 'var(--critical)',
};

export function Calendar() {
  const events = useCalendar((s) => s.events);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const upcoming = events.filter((e) => e.startTimeMs > now - 60_000).slice().sort((a, b) => a.startTimeMs - b.startTimeMs);

  return (
    <div className="flex h-full flex-col bg-[var(--bg-deep)]">
      <header className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 py-3">
        <span className="text-mono-md font-bold text-[var(--text-primary)]">Calendar</span>
        <span className="text-mono-xs text-[var(--text-muted)]">{upcoming.length} upcoming</span>
      </header>
      <div className="flex-1 overflow-y-auto p-3">
        {upcoming.length === 0 ? (
          <p className="p-4 text-center text-mono-xs text-[var(--text-muted)]">no upcoming events. silence is rare. enjoy it.</p>
        ) : (
          upcoming.map((e) => <EventRow key={e.id} event={e} now={now} />)
        )}
      </div>
    </div>
  );
}

function EventRow({ event, now }: { event: CalendarEvent; now: number }) {
  const remainingMs = event.startTimeMs - now;
  const color = CATEGORY_COLOR[event.category];

  return (
    <div className="mb-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-mono-sm font-bold" style={{ color }}>{event.title}</span>
        <span className="text-mono-xs tabular-nums text-[var(--text-muted)]">{formatCountdown(remainingMs)}</span>
      </div>
      <p className="mt-1 text-mono-xs text-[var(--text-secondary)]">{event.description}</p>
      <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
        <span className={cn('inline-block h-1.5 w-1.5 rounded-full')} style={{ background: color }} />
        <span>{event.category}</span>
        {event.durationMin > 0 && <span>· {event.durationMin}min</span>}
      </div>
    </div>
  );
}

function formatCountdown(ms: number): string {
  if (ms < 0) return 'now';
  const sec = Math.floor(ms / 1000);
  const min = Math.floor(sec / 60);
  const hour = Math.floor(min / 60);
  const day = Math.floor(hour / 24);
  if (day > 0) return `in ${day}d ${hour % 24}h`;
  if (hour > 0) return `in ${hour}h ${min % 60}m`;
  if (min > 0) return `in ${min}m`;
  return `in ${sec}s`;
}
